import { useEffect, useState } from "react";
import { AlertTriangle, Link2, Loader2, Search, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { formatApiError, isApiError } from "@/lib/api-error";
import { financeApi } from "@/services/finance-api";
import {
  useCadastrarClienteOmie,
  useOmieClientes,
  useRascunhoClienteOmie,
  useVincularDaProposta,
} from "@/hooks/use-finance-omie";
import type {
  ClienteOmieFormulario,
  ClienteOmieGuardado,
  ClientesOmieExistentes,
  OmieEmpresaChave,
  OmieTipoCadastro,
} from "@/types/finance";

interface CadastroClienteOmieDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tipo: OmieTipoCadastro;
  cadastroId: string;
  nome: string;
  empresa: OmieEmpresaChave;
  empresaNome: string;
  cadastroHabilitado: boolean;
}

type CampoDoFormulario = keyof ClienteOmieFormulario;

const FORMULARIO_VAZIO: ClienteOmieFormulario = {
  razao_social: null,
  nome_fantasia: null,
  cnpj_cpf: null,
  email: null,
  telefone: null,
  cep: null,
  logradouro: null,
  numero: null,
  complemento: null,
  bairro: null,
  cidade: null,
  uf: null,
  codigo_ibge: null,
};

const CAMPOS: Array<{ campo: CampoDoFormulario; rotulo: string; largura: string; obrigatorio?: boolean }> = [
  { campo: "razao_social", rotulo: "Razão social ou nome", largura: "sm:col-span-6", obrigatorio: true },
  { campo: "nome_fantasia", rotulo: "Nome fantasia", largura: "sm:col-span-3" },
  { campo: "cnpj_cpf", rotulo: "CNPJ ou CPF", largura: "sm:col-span-3" },
  { campo: "email", rotulo: "E-mail", largura: "sm:col-span-4" },
  { campo: "telefone", rotulo: "Telefone", largura: "sm:col-span-2" },
  { campo: "cep", rotulo: "CEP", largura: "sm:col-span-2" },
  { campo: "logradouro", rotulo: "Endereço", largura: "sm:col-span-4" },
  { campo: "numero", rotulo: "Número", largura: "sm:col-span-2" },
  { campo: "complemento", rotulo: "Complemento", largura: "sm:col-span-4" },
  { campo: "bairro", rotulo: "Bairro", largura: "sm:col-span-2" },
  { campo: "cidade", rotulo: "Cidade", largura: "sm:col-span-3" },
  { campo: "uf", rotulo: "UF", largura: "sm:col-span-1" },
];

function digitos(valor: string | null): string {
  return (valor ?? "").replace(/\D/g, "");
}

function digitoVerificador(base: string, pesos: number[]): number {
  const soma = base.split("").reduce((total, digito, indice) => total + Number(digito) * pesos[indice], 0);
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

/** Espelha document_is_valid da API: null sem documento, false quando os dígitos verificadores não batem. */
export function documentoValido(valor: string | null): boolean | null {
  const numeros = digitos(valor);
  if (!numeros) return null;
  if (/^(\d)\1+$/.test(numeros)) return false;
  if (numeros.length === 14) {
    const pesos = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const primeiro = digitoVerificador(numeros.slice(0, 12), pesos.slice(1));
    const segundo = digitoVerificador(numeros.slice(0, 12) + primeiro, pesos);
    return numeros.endsWith(`${primeiro}${segundo}`);
  }
  if (numeros.length === 11) {
    const primeiro = digitoVerificador(numeros.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2]);
    const segundo = digitoVerificador(numeros.slice(0, 9) + primeiro, [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
    return numeros.endsWith(`${primeiro}${segundo}`);
  }
  return false;
}

function clientesExistentes(erro: unknown): ClientesOmieExistentes | null {
  if (!isApiError(erro) || erro.status !== 409 || !erro.body) return null;
  try {
    const corpo = JSON.parse(erro.body) as { detail?: ClientesOmieExistentes };
    return Array.isArray(corpo.detail?.clientes) ? corpo.detail : null;
  } catch {
    return null;
  }
}

/**
 * Resolve o "sem vínculo com o Omie" da OS: liga um cliente que já existe no Omie da empresa,
 * ou cria o cliente lá com os dados do nosso cadastro. O formulário grava no nosso cadastro
 * antes de enviar, então o que for digitado aqui não se perde se o Omie recusar.
 */
export function CadastroClienteOmieDialog({
  open,
  onOpenChange,
  tipo,
  cadastroId,
  nome,
  empresa,
  empresaNome,
  cadastroHabilitado,
}: CadastroClienteOmieDialogProps) {
  const { toast } = useToast();
  const [aba, setAba] = useState<"vincular" | "enviar">("vincular");
  const [termoBusca, setTermoBusca] = useState(nome);
  const [termoDebounced, setTermoDebounced] = useState(nome);
  const [formulario, setFormulario] = useState<ClienteOmieFormulario>(FORMULARIO_VAZIO);
  const [existentes, setExistentes] = useState<ClienteOmieGuardado[] | null>(null);
  const [buscandoCep, setBuscandoCep] = useState(false);

  const rascunho = useRascunhoClienteOmie({ tipo, id: cadastroId, empresa }, open && cadastroHabilitado);
  const clientes = useOmieClientes({ vinculo: "todos", q: termoDebounced, page: 1, page_size: 50 });
  const cadastrar = useCadastrarClienteOmie();
  const vincular = useVincularDaProposta();

  useEffect(() => {
    if (!open) return;
    setAba("vincular");
    setTermoBusca(nome);
    setTermoDebounced(nome);
    setExistentes(null);
  }, [open, nome]);

  useEffect(() => {
    if (rascunho.data) setFormulario(rascunho.data.formulario);
  }, [rascunho.data]);

  useEffect(() => {
    const timer = setTimeout(() => setTermoDebounced(termoBusca), 300);
    return () => clearTimeout(timer);
  }, [termoBusca]);

  const semVinculo = (clientes.data?.items ?? []).filter(
    (cliente) => cliente.omie_empresa.chave === empresa && cliente.vinculo === null && !cliente.inativo,
  );

  const mostrarErro = (erro: unknown) => {
    const formatado = formatApiError(erro);
    toast({ variant: "destructive", title: formatado.title, description: formatado.detail });
  };

  const vincularCliente = async (clienteId: string) => {
    try {
      await vincular.mutateAsync({
        clienteId,
        body: { origem: "manual", ...(tipo === "agencia" ? { agencia_id: cadastroId } : { freelancer_id: cadastroId }) },
      });
      toast({ title: "Vínculo registrado" });
      onOpenChange(false);
    } catch (erro) {
      mostrarErro(erro);
    }
  };

  const enviar = async (ignorarExistentes: boolean) => {
    try {
      const cliente = await cadastrar.mutateAsync({
        tipo,
        id: cadastroId,
        body: { empresa, formulario, ignorar_existentes: ignorarExistentes },
      });
      toast({ title: `Cliente criado no Omie da ${empresaNome}`, description: `Código ${cliente.codigo_cliente_omie}` });
      onOpenChange(false);
    } catch (erro) {
      const encontrados = clientesExistentes(erro);
      if (encontrados) {
        setExistentes(encontrados.clientes);
        return;
      }
      mostrarErro(erro);
    }
  };

  const alterarCampo = (campo: CampoDoFormulario, valor: string) =>
    setFormulario((atual) => ({ ...atual, [campo]: valor || null }));

  const completarPeloCep = async () => {
    const cep = digitos(formulario.cep);
    if (cep.length !== 8) return;
    setBuscandoCep(true);
    try {
      const endereco = await financeApi.buscarCep(cep);
      setFormulario((atual) => ({
        ...atual,
        cep: endereco.cep,
        logradouro: endereco.logradouro ?? atual.logradouro,
        bairro: endereco.bairro ?? atual.bairro,
        cidade: endereco.cidade ?? atual.cidade,
        uf: endereco.uf ?? atual.uf,
        codigo_ibge: endereco.codigo_ibge ?? atual.codigo_ibge,
      }));
    } catch {
      toast({ title: "CEP não encontrado", description: "Preencha o endereço à mão." });
    } finally {
      setBuscandoCep(false);
    }
  };

  const validade = documentoValido(formulario.cnpj_cpf);
  const enviando = cadastrar.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Cliente no Omie da {empresaNome}</DialogTitle>
          <DialogDescription>
            {nome} ainda não tem cliente vinculado no Omie da {empresaNome}. Vincule um que já exista lá, ou envie o
            cadastro para criar.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={aba} onValueChange={(valor) => setAba(valor as "vincular" | "enviar")}>
          <TabsList>
            <TabsTrigger value="vincular">Vincular existente</TabsTrigger>
            <TabsTrigger value="enviar">Enviar ao Omie</TabsTrigger>
          </TabsList>

          <TabsContent value="vincular" className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={termoBusca}
                onChange={(evento) => setTermoBusca(evento.target.value)}
                placeholder="Buscar por nome, e-mail ou CNPJ..."
                className="pl-9"
              />
            </div>
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {clientes.isLoading && (
                <div className="flex items-center justify-center py-6 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
              {!clientes.isLoading && semVinculo.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Nenhum cliente sem vínculo no Omie da {empresaNome} com essa busca.
                </p>
              )}
              {semVinculo.map((cliente) => (
                <button
                  key={cliente.id}
                  type="button"
                  disabled={vincular.isPending}
                  onClick={() => vincularCliente(cliente.id)}
                  className="flex w-full flex-col items-start rounded-md border p-2.5 text-left text-sm hover:bg-muted disabled:opacity-50"
                >
                  <span className="font-medium">
                    {cliente.razao_social}
                    {cliente.nome_fantasia ? ` (${cliente.nome_fantasia})` : ""}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {[cliente.cnpj_cpf, cliente.email].filter(Boolean).join(" · ") || "Sem CNPJ e e-mail no Omie"}
                  </span>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="enviar" className="space-y-3">
            {!cadastroHabilitado && (
              <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                O cadastro de clientes no Omie está desligado na Plataforma. Até ser ligado, crie o cliente direto no
                Omie e vincule na outra aba.
              </p>
            )}
            {cadastroHabilitado && rascunho.isLoading && (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
            {cadastroHabilitado && rascunho.isError && (
              <p className="text-sm text-destructive">{formatApiError(rascunho.error).detail}</p>
            )}
            {cadastroHabilitado && rascunho.data && existentes && (
              <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="flex items-center gap-2 font-medium">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  O Omie da {empresaNome} já tem cliente com este documento
                </p>
                {existentes.map((cliente) => (
                  <div key={cliente.id} className="flex flex-wrap items-center gap-2 rounded border bg-background p-2">
                    <span className="min-w-0 flex-1">
                      {cliente.razao_social ?? "Sem nome"} · {cliente.cnpj_cpf ?? "-"}
                      {(cliente.agencia_id || cliente.freelancer_id) && " · já vinculado a outro cadastro"}
                    </span>
                    {!cliente.agencia_id && !cliente.freelancer_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 px-2 text-xs"
                        disabled={vincular.isPending}
                        onClick={() => vincularCliente(cliente.id)}
                      >
                        <Link2 className="h-3 w-3" />
                        Vincular este
                      </Button>
                    )}
                  </div>
                ))}
                <Button size="sm" variant="ghost" disabled={enviando} onClick={() => enviar(true)}>
                  Criar outro cliente mesmo assim
                </Button>
              </div>
            )}
            {cadastroHabilitado && rascunho.data && !existentes && (
              <>
                <div className="grid gap-3 sm:grid-cols-6">
                  {CAMPOS.map(({ campo, rotulo, largura, obrigatorio }) => (
                    <div key={campo} className={`space-y-1 ${largura}`}>
                      <Label htmlFor={`omie-${campo}`} className="text-xs">
                        {rotulo}
                        {obrigatorio && " *"}
                      </Label>
                      <Input
                        id={`omie-${campo}`}
                        value={formulario[campo] ?? ""}
                        onChange={(evento) => alterarCampo(campo, evento.target.value)}
                        onBlur={campo === "cep" ? completarPeloCep : undefined}
                        maxLength={campo === "uf" ? 2 : undefined}
                      />
                    </div>
                  ))}
                </div>
                {buscandoCep && <p className="text-xs text-muted-foreground">Buscando o endereço do CEP...</p>}
                {validade === false && (
                  <p className="flex items-center gap-2 text-xs text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    Os dígitos verificadores deste documento não batem. O Omie pode recusar.
                  </p>
                )}
                {validade === null && (
                  <p className="text-xs text-muted-foreground">Sem CNPJ ou CPF, o cliente é criado só com o nome.</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Os dados ficam salvos no cadastro {tipo === "agencia" ? "da agência" : "do freelancer"} antes do
                  envio. Nome, documento, e-mail e telefone só preenchem o que estiver vazio lá.
                </p>
              </>
            )}
          </TabsContent>
        </Tabs>

        {aba === "enviar" && cadastroHabilitado && rascunho.data && !existentes && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              className="gap-1.5"
              disabled={enviando || !formulario.razao_social}
              onClick={() => enviar(false)}
            >
              {enviando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Criar no Omie da {empresaNome}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
